import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from 'components/ui/dialog'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Textarea } from 'components/ui/textarea'
import { Label } from 'components/ui/label'
import { Select } from 'components/ui/select'
import { GitBranch, MapPin } from 'lucide-react'
import { createProject } from 'slices/projectSlice'
import { initializeActiveLocations } from 'slices/locationSlice'
import { fetchLookupLists } from 'slices/lookupListSlice'
import routes from 'constants/routes'

// Format location display label: "Asset Name - Address, City, ST"
function formatLocationLabel(location: any): string {
	if (!location) return ''
	const name = location.assetName || 'Unnamed Location'
	const cityState = [location.city, location.stateProvince].filter(Boolean).join(', ')
	const detail = [location.address, cityState].filter(Boolean).join(', ')
	return detail ? `${name} - ${detail}` : name
}

interface CreateProjectModalProps {
	open: boolean
	onClose: () => void
	onCreated?: (project: any) => void
}

const CreateProjectModal = ({ open, onClose, onCreated }: CreateProjectModalProps) => {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const { userInfo } = useSelector((state: any) => state.userLogin)
	const {
		entities: locationEntities,
		ids: locationIds,
		isLoading: locationsLoading,
	} = useSelector((state: any) => state.locations)
	const {
		entities: lookupEntities,
		ids: lookupIds,
		isInitialized: lookupLoaded,
	} = useSelector((state: any) => state.lookupLists)

	const [projectName, setProjectName] = useState('')
	const [projectDescription, setProjectDescription] = useState('')
	const [projectType, setProjectType] = useState('')
	const [projectAsset, setProjectAsset] = useState('')
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [errorMessage, setErrorMessage] = useState<string | null>(null)

	// Fetch on open. Locations are fetched unconditionally: several thunks
	// (archived, tenants, my-centers) write the same ids array, so
	// `isInitialized` is not a reliable signal that it holds active locations.
	useEffect(() => {
		if (!open) return
		dispatch(initializeActiveLocations() as any)
		if (!lookupLoaded) dispatch(fetchLookupLists(undefined) as any)
	}, [open, dispatch, lookupLoaded])

	// `/api/locations/active` merges two queries and can emit the same location
	// twice, so dedupe by _id before rendering options.
	const locationOptions = useMemo(() => {
		const seen = new Set<string>()
		return locationIds
			.map((id: string) => locationEntities[id])
			.filter((loc: any) => {
				if (!loc || !loc._id || seen.has(loc._id)) return false
				seen.add(loc._id)
				return true
			})
			.sort((a: any, b: any) => (a.assetName || '').localeCompare(b.assetName || ''))
	}, [locationEntities, locationIds])

	const projectTypeOptions = useMemo(
		() =>
			lookupIds
				.map((id: string) => lookupEntities[id])
				.filter((item: any) => item && item.listType === 'project')
				.map((item: any) => item.option)
				.sort((a: string, b: string) => a.localeCompare(b)),
		[lookupEntities, lookupIds],
	)

	const hasNoLocations = locationOptions.length === 0 && !locationsLoading

	const resetAndClose = () => {
		setProjectName('')
		setProjectDescription('')
		setProjectType('')
		setProjectAsset('')
		setErrorMessage(null)
		onClose()
	}

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		if (!projectName.trim() || !projectAsset) return

		setIsSubmitting(true)
		setErrorMessage(null)
		try {
			const result: any = await dispatch(
				createProject({
					owner: userInfo._id,
					projectName: projectName.trim(),
					projectDescription: projectDescription.trim() || undefined,
					projectType: projectType || undefined,
					projectAsset,
					updateNote: 'New Project',
				}) as any,
			)

			if (result?.meta?.requestStatus === 'rejected' || !result?.payload?._id) {
				// Keep the dialog open so the user does not lose their input.
				setErrorMessage(
					typeof result?.payload === 'string' ? result.payload : 'Failed to create project. Please try again.',
				)
				return
			}

			onCreated?.(result.payload)
			resetAndClose()
		} finally {
			setIsSubmitting(false)
		}
	}

	return (
		<Dialog open={open} onClose={resetAndClose}>
			<DialogContent>
				<DialogHeader>
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
							<GitBranch className="w-5 h-5" />
						</div>
						<div>
							<DialogTitle>New Project</DialogTitle>
							<DialogDescription>Add a project to one of your locations</DialogDescription>
						</div>
					</div>
				</DialogHeader>

				{hasNoLocations ? (
					<div className="p-5">
						<div className="flex flex-col items-center text-center py-6">
							<div className="w-16 h-16 rounded-full flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe] mb-4">
								<MapPin className="w-8 h-8" />
							</div>
							<h3 className="text-base font-semibold text-gray-900 mb-1">No Locations Yet</h3>
							<p className="text-sm text-gray-500 max-w-sm">
								Projects are attached to a location. Create your first location, then come back to add a
								project to it.
							</p>
							<Button
								type="button"
								className="mt-5"
								onClick={() => {
									resetAndClose()
									navigate(routes.LOCATIONS)
								}}
							>
								Go to Locations
							</Button>
						</div>
						<DialogFooter className="px-0 justify-center border-t border-gray-200 pt-4">
							<Button type="button" variant="ghost" onClick={resetAndClose}>
								Cancel
							</Button>
						</DialogFooter>
					</div>
				) : (
					<form onSubmit={handleSubmit} className="p-5 space-y-4">
						{/* Project Name */}
						<div className="space-y-1.5">
							<Label htmlFor="projectName">
								Project Name <span className="text-red-500">*</span>
							</Label>
							<Input
								id="projectName"
								type="text"
								value={projectName}
								onChange={e => setProjectName(e.target.value)}
								placeholder="e.g., Ground-up development, Lease renewal"
								autoFocus
								required
							/>
						</div>

						{/* Location */}
						<div className="space-y-1.5">
							<Label htmlFor="projectAsset">
								Location <span className="text-red-500">*</span>
							</Label>
							<Select
								id="projectAsset"
								value={projectAsset}
								onChange={e => setProjectAsset(e.target.value)}
								required
							>
								<option value="">Select a location</option>
								{locationOptions.map((location: any) => (
									<option key={location._id} value={location._id}>
										{formatLocationLabel(location)}
									</option>
								))}
							</Select>
						</div>

						{/* Project Type */}
						<div className="space-y-1.5">
							<Label htmlFor="projectType">
								Project Type <span className="text-gray-400">(Optional)</span>
							</Label>
							<Select id="projectType" value={projectType} onChange={e => setProjectType(e.target.value)}>
								<option value="">Select type...</option>
								{projectTypeOptions.map((option: string) => (
									<option key={option} value={option}>
										{option}
									</option>
								))}
							</Select>
						</div>

						{/* Description */}
						<div className="space-y-1.5">
							<Label htmlFor="projectDescription">
								Description <span className="text-gray-400">(Optional)</span>
							</Label>
							<Textarea
								id="projectDescription"
								value={projectDescription}
								onChange={e => setProjectDescription(e.target.value)}
								placeholder="Add any additional context or notes..."
								className="min-h-[80px] resize-none"
								rows={3}
							/>
						</div>

						{errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}

						{/* Actions, centred, matching the other add-asset popups. */}
						<DialogFooter className="px-0 justify-center border-t border-gray-200 pt-4">
							<Button type="button" variant="ghost" onClick={resetAndClose}>
								Cancel
							</Button>
							<Button
								type="submit"
								className="gap-2"
								disabled={!projectName.trim() || !projectAsset || isSubmitting}
							>
								<GitBranch className="w-4 h-4" />
								{isSubmitting ? 'Creating...' : 'Add Project'}
							</Button>
						</DialogFooter>
					</form>
				)}
			</DialogContent>
		</Dialog>
	)
}

export default CreateProjectModal
